<template>
  <div class="loan-report-page">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ summary.totalStaff || 0 }} Staff
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading loan report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else-if="!departments.length" class="report-page text-center py-5">
      <header class="report-main-header text-center">
        <h1 class="org-name">{{ campusName }}</h1>
        <h2 v-if="campusBranch" class="campus-title">{{ campusBranch }}</h2>
        <h3 class="report-title">{{ reportTitle.toUpperCase() }}</h3>
      </header>
      <h5 class="text-secondary mt-4">
        No {{ loanTypeLabel.toLowerCase() }} records found for the selected
        criteria
      </h5>
    </div>

    <div v-else class="report-page">
      <header class="report-main-header text-center">
        <div v-if="schoolLogo" class="logo-center">
          <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
        </div>
        <h1 class="org-name">{{ campusName }}</h1>
        <h2 v-if="campusBranch" class="campus-title">{{ campusBranch }}</h2>
        <h3 class="report-title">{{ reportTitle.toUpperCase() }}</h3>
        <div class="meta-line">
          <span>{{ loanTypeLabel }}</span>
          <span class="dot">·</span>
          <span>{{ departmentLabel }}</span>
          <span class="dot">·</span>
          <span>{{ currentDateTime }}</span>
        </div>
      </header>

      <div class="summary-strip">
        <div class="summary-item">
          <span class="s-label">Staff</span>
          <span class="s-value">{{ summary.totalStaff || 0 }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">{{ amountHeading }}</span>
          <span class="s-value">{{
            formatCurrency(summary.totalLoanAmount)
          }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">Returned</span>
          <span class="s-value returned">{{
            formatCurrency(summary.totalReturned)
          }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">Outstanding</span>
          <span class="s-value outstanding">{{
            formatCurrency(summary.totalBalance)
          }}</span>
        </div>
      </div>

      <section
        v-for="(dept, deptIndex) in departmentViews"
        :key="'dept-' + dept.departmentID + '-' + deptIndex"
        class="department-section"
      >
        <table class="loan-table">
          <thead>
            <tr class="row-department">
              <th :colspan="colCount">
                <span class="dept-name">{{
                  dept.departmentName || "Unassigned"
                }}</span>
                <span class="dept-meta">{{ dept.staffCount }} Staff</span>
              </th>
            </tr>
            <tr class="row-columns">
              <th class="col-sno">#</th>
              <th class="col-code">Code</th>
              <th class="col-name">Staff Name</th>
              <th class="col-father">Father Name</th>
              <th class="col-desig">Designation</th>
              <th class="col-date">Date</th>
              <th v-if="showTypeCol" class="col-type">Type</th>
              <th class="col-amt">Amount</th>
              <th class="col-amt">Returned</th>
              <th class="col-amt">Balance</th>
              <th class="col-month">Mths</th>
              <th class="col-inst">Installment</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="dept.rows.length">
              <tr
                v-for="(row, rowIndex) in dept.rows"
                :key="'row-' + deptIndex + '-' + rowIndex"
                :class="{
                  'first-of-staff': row.isFirstOfStaff,
                  'continue-staff': !row.isFirstOfStaff,
                }"
              >
                <td class="cell-sno text-center">
                  {{ row.isFirstOfStaff ? row.sno : "" }}
                </td>
                <td class="cell-code text-center">
                  {{ row.isFirstOfStaff ? row.empCode : "" }}
                </td>
                <td class="cell-name">
                  {{ row.isFirstOfStaff ? row.name : "" }}
                </td>
                <td class="cell-father">
                  {{ row.isFirstOfStaff ? row.fatherName : "" }}
                </td>
                <td class="cell-desig">
                  {{ row.isFirstOfStaff ? row.designation : "" }}
                </td>
                <td class="cell-date text-center">{{ row.date }}</td>
                <td v-if="showTypeCol" class="cell-type text-center">
                  {{ formatLoanType(row.loanType) }}
                </td>
                <td class="cell-amt text-right">
                  {{ formatCurrency(row.amount) }}
                </td>
                <td class="cell-amt text-right">
                  {{ formatCurrency(row.returned) }}
                </td>
                <td class="cell-balance text-right">
                  {{ formatCurrency(row.balance) }}
                </td>
                <td class="cell-month text-center">
                  {{ row.months != null && row.months !== "" ? row.months : "—" }}
                </td>
                <td class="cell-inst text-right">
                  {{
                    row.monthlyInstallments
                      ? formatCurrency(row.monthlyInstallments)
                      : "—"
                  }}
                </td>
              </tr>
            </template>
            <tr v-else>
              <td :colspan="colCount" class="text-center empty-row">
                No staff detail available
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="row-summary">
              <td :colspan="summaryLabelSpan" class="summary-label">
                Department Total — {{ dept.departmentName || "Unassigned" }}
              </td>
              <td class="text-right">{{ formatCurrency(dept.loanAmount) }}</td>
              <td class="text-right">{{ formatCurrency(dept.returned) }}</td>
              <td class="text-right">{{ formatCurrency(dept.balance) }}</td>
              <td colspan="2" />
            </tr>
          </tfoot>
        </table>
      </section>

      <table class="grand-total-table">
        <tbody>
          <tr>
            <td class="gt-label">Grand Total</td>
            <td class="gt-staff">{{ summary.totalStaff || 0 }} Staff</td>
            <td class="gt-amt text-right">
              Amount:
              <strong>{{ formatCurrency(summary.totalLoanAmount) }}</strong>
            </td>
            <td class="gt-amt text-right">
              Returned:
              <strong>{{ formatCurrency(summary.totalReturned) }}</strong>
            </td>
            <td class="gt-amt gt-balance text-right">
              Balance:
              <strong>{{ formatCurrency(summary.totalBalance) }}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="report-footer">
        <div class="sign-box">
          <div class="sign-line" />
          <span>Prepared By</span>
        </div>
        <div class="sign-box">
          <div class="sign-line" />
          <span>Checked By</span>
        </div>
        <div class="sign-box">
          <div class="sign-line" />
          <span>Approved By</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "StaffLoanAdvanceReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      departmentLabel: "All Departments",
      db: "",
      cID: "",
      depID: 0,
      loanType: "loan",
      summary: {
        totalStaff: 0,
        totalLoanAmount: 0,
        totalReturned: 0,
        totalBalance: 0,
      },
      departments: [],
    };
  },
  computed: {
    reportTitle() {
      if (this.loanType === "advance") return "Staff Advance Report";
      if (this.loanType === "all") return "Staff Loan & Advance Report";
      return "Staff Loan Report";
    },
    loanTypeLabel() {
      if (this.loanType === "advance") return "Advance";
      if (this.loanType === "all") return "Loan & Advance";
      return "Loan";
    },
    amountHeading() {
      if (this.loanType === "advance") return "Advance Amount";
      if (this.loanType === "all") return "Total Amount";
      return "Loan Amount";
    },
    showTypeCol() {
      return this.loanType === "all";
    },
    colCount() {
      return this.showTypeCol ? 12 : 11;
    },
    summaryLabelSpan() {
      return this.showTypeCol ? 7 : 6;
    },
    departmentViews() {
      return this.departments.map((dept) => {
        const built = this.buildDeptRows(dept);
        return {
          ...dept,
          rows: built.rows,
          staffCount: Number(dept.staffCount) || built.staffCount,
        };
      });
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query || {};
      this.db = query.db || this.$store.state.userData?.db || "";
      this.cID =
        query.cID != null && query.cID !== ""
          ? query.cID
          : this.$store.state.userData?.cId;
      this.depID = Number(query.depID || 0);
      const lt = String(query.loanType || "loan").toLowerCase();
      this.loanType = ["loan", "advance", "all"].includes(lt) ? lt : "loan";
      this.departmentLabel = query.depName
        ? String(query.depName)
        : this.depID
        ? `Department #${this.depID}`
        : "All Departments";

      this.campusName =
        this.$store.state.currentBranch?.branch ||
        this.$store.state.currentBranch?.name ||
        "My Skool";
      this.schoolLogo = this.$store.state.currentBranch?.logo || null;

      if (
        !this.db ||
        this.cID === undefined ||
        this.cID === null ||
        this.cID === ""
      ) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(`${this.$store.state.domain}campuses`, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
          timeout: 60000,
        });

        const payload = response?.data?.data || response?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        const match = list.find((c) => c.id == this.cID);
        if (match) {
          this.campusName = match.name || match.branch || this.campusName;
          this.campusBranch =
            this.cID == 0 ? "All Campuses" : match.branch || "";
          this.schoolLogo = match.logo || this.schoolLogo;
        }
      } catch (e) {
        // Keep store fallback
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          db: this.db,
          cID: this.cID,
          loanType: this.loanType,
        };
        if (this.depID && this.depID !== 0) {
          params.depID = this.depID;
        }

        const response = await axios.get(
          `${this.$store.state.domain}Staff/LoanReportDepartmentWise`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
            timeout: 120000,
          }
        );

        const data = response?.data || {};
        if (data.status && data.status !== "success") {
          throw new Error(data.message || "Failed to load report");
        }

        this.summary = {
          totalStaff: Number(data.totalStaff) || 0,
          totalLoanAmount: Number(data.totalLoanAmount) || 0,
          totalReturned: Number(data.totalReturned) || 0,
          totalBalance: Number(data.totalBalance) || 0,
        };

        this.departments = Array.isArray(data.departments)
          ? data.departments.filter(
              (d) =>
                (Number(d.staffCount) || 0) > 0 ||
                (Array.isArray(d.staff) && d.staff.length > 0) ||
                (Number(d.loanAmount) || 0) > 0 ||
                (Number(d.balance) || 0) > 0
            )
          : [];
      } catch (err) {
        console.error("Loan report error", err);
        this.error =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load loan report.";
        this.departments = [];
      } finally {
        this.loading = false;
      }
    },
    buildDeptRows(dept) {
      const rows = [];
      const staffList = Array.isArray(dept?.staff) ? dept.staff : [];

      staffList.forEach((staff, index) => {
        const loans = Array.isArray(staff.loans) ? staff.loans : [];
        const empCode =
          staff.empCodeStr ||
          staff.rfID ||
          staff.empCode ||
          staff.staffID ||
          "—";
        const base = {
          sno: staff.sno || index + 1,
          empCode,
          name: this.formatName(staff.name),
          fatherName: this.formatName(staff.father_name || staff.fatherName),
          designation: staff.designation || "—",
        };

        if (!loans.length) {
          rows.push({
            ...base,
            isFirstOfStaff: true,
            date: "—",
            loanType: "",
            amount: staff.loanAmount || 0,
            returned: staff.returned || 0,
            balance: staff.balance || 0,
            months: "",
            monthlyInstallments: "",
          });
          return;
        }

        loans.forEach((loan, loanIndex) => {
          rows.push({
            ...base,
            isFirstOfStaff: loanIndex === 0,
            date: this.formatDate(loan.date),
            loanType: loan.loanType || this.loanType,
            amount: loan.amount,
            returned: loan.returned,
            balance: loan.balance,
            months: loan.months,
            monthlyInstallments: loan.monthlyInstallments,
          });
        });
      });

      return {
        rows,
        staffCount: staffList.length,
      };
    },
    formatName(value) {
      if (!value) return "—";
      return String(value)
        .trim()
        .replace(/\s+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatLoanType(type) {
      const t = String(type || "").toLowerCase();
      if (t === "advance") return "Advance";
      if (t === "loan") return "Loan";
      return type || "—";
    },
    formatCurrency(value) {
      const n = parseFloat(value);
      if (!Number.isFinite(n)) return "0.00";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.loan-report-page {
  --ink: #111;
  --border: #111;
  --muted: #555;
  --soft: #f4f5f7;

  background: #eef0f3;
  min-height: 100vh;
  padding: 2.5rem;
  color: var(--ink);
  font-family: "Outfit", "Inter", "Segoe UI", Calibri, Arial, sans-serif;
}

.control-bar {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  padding: 1.15rem 2rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;

  h4 {
    color: #333;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  span {
    margin-left: 4px;
  }
}

.btn-print {
  background: linear-gradient(135deg, #4b5563 0%, #1f2937 100%);
  color: #fff;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(31, 41, 55, 0.28);
  }
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #c0392b;
    border-color: #fab1a0;
  }
}

.report-page {
  background: #fff;
  padding: 2.75rem 2.25rem;
  border-radius: 10px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.1);
  width: 98%;
  max-width: 1280px;
  margin: 20px auto;
  box-sizing: border-box;
}

.report-main-header {
  margin-bottom: 1.25rem;

  .logo-center {
    margin-bottom: 0.65rem;

    .school-logo-img {
      height: 64px;
      width: auto;
      object-fit: contain;
    }
  }

  .org-name,
  .campus-title,
  .report-title {
    margin: 0;
    font-weight: 700;
    text-transform: uppercase;
    color: #000;
    line-height: 1.25;
  }

  .org-name {
    font-size: 1.5rem;
    letter-spacing: 0.04em;
  }

  .campus-title {
    font-size: 1.1rem;
    margin-top: 0.15rem;
  }

  .report-title {
    font-size: 0.98rem;
    margin-top: 0.45rem;
    letter-spacing: 0.14em;
  }

  .meta-line {
    margin-top: 0.55rem;
    font-size: 0.82rem;
    color: var(--muted);
    font-weight: 600;

    .dot {
      margin: 0 0.4rem;
      opacity: 0.55;
    }
  }
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1.5px solid var(--border);
  margin-bottom: 1.35rem;

  .summary-item {
    padding: 0.7rem 0.85rem;
    text-align: center;
    border-right: 1px solid #bbb;

    &:last-child {
      border-right: none;
    }

    .s-label {
      display: block;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 0.2rem;
    }

    .s-value {
      display: block;
      font-size: 1.05rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;

      &.returned {
        color: #1e7a46;
      }

      &.outstanding {
        color: #b91c1c;
      }
    }
  }
}

.department-section {
  margin-bottom: 1.4rem;
  page-break-inside: auto;
}

.loan-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.8rem;
  border: 1.5px solid var(--border);

  thead {
    display: table-header-group;
  }

  tfoot {
    display: table-footer-group;
  }

  tbody tr {
    page-break-inside: avoid;
  }

  th,
  td {
    border: 1px solid #222;
    padding: 0.38rem 0.45rem;
    vertical-align: middle;
    color: #000;
    word-break: break-word;
  }

  .row-department th {
    background: #111;
    color: #fff;
    text-align: left;
    padding: 0.55rem 0.7rem;

    .dept-name {
      font-size: 0.9rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-right: 0.75rem;
    }

    .dept-meta {
      font-size: 0.75rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.78);
    }
  }

  .row-columns th {
    background: #fff;
    text-align: center;
    font-size: 0.68rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.42rem 0.3rem;
  }

  .col-sno {
    width: 3.5%;
  }
  .col-code {
    width: 7%;
  }
  .col-name {
    width: 14%;
  }
  .col-father {
    width: 13%;
  }
  .col-desig {
    width: 12%;
  }
  .col-date {
    width: 9%;
  }
  .col-type {
    width: 7%;
  }
  .col-amt {
    width: 9%;
  }
  .col-month {
    width: 5%;
  }
  .col-inst {
    width: 9%;
  }

  .cell-name,
  .cell-father,
  .cell-desig {
    font-weight: 600;
  }

  .cell-sno,
  .cell-code,
  .cell-date,
  .cell-type,
  .cell-month {
    font-weight: 600;
  }

  .cell-amt,
  .cell-balance,
  .cell-inst {
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
  }

  .cell-balance {
    color: #b91c1c;
  }

  .first-of-staff td {
    border-top: 1.5px solid #111;
  }

  .continue-staff td {
    background: #fafafa;
  }

  .empty-row {
    padding: 1rem;
    color: #666;
    font-weight: 600;
  }

  .row-summary td {
    background: #f3f4f6;
    font-weight: 800;
    border-top: 2px solid #111;
    padding: 0.5rem 0.45rem;
  }

  .summary-label {
    text-align: right;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
  }
}

.grand-total-table {
  width: 100%;
  border-collapse: collapse;
  border: 2px solid #111;
  margin-top: 0.35rem;
  margin-bottom: 2rem;

  td {
    border: 1px solid #111;
    padding: 0.7rem 0.85rem;
    font-weight: 800;
    vertical-align: middle;
  }

  .gt-label {
    width: 18%;
    background: #111;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.82rem;
  }

  .gt-staff {
    width: 14%;
    background: #f3f4f6;
    font-size: 0.85rem;
  }

  .gt-amt {
    font-variant-numeric: tabular-nums;
    font-size: 0.88rem;
    white-space: nowrap;

    strong {
      font-size: 0.95rem;
      margin-left: 0.25rem;
    }
  }

  .gt-balance {
    color: #b91c1c;
  }
}

.report-footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  margin-top: 2.5rem;
  padding-top: 0.5rem;
  width: 100%;

  .sign-box {
    flex: 1 1 0;
    text-align: center;
    min-width: 0;

    .sign-line {
      border-top: 1.5px solid #222;
      margin: 2.4rem 0.75rem 0.45rem;
    }

    span {
      display: block;
      font-size: 0.78rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #333;
    }
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .loan-report-page {
    background: #fff;
    padding: 0;
  }

  .report-page {
    box-shadow: none;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0.35rem;
    border-radius: 0;
  }

  .department-section {
    page-break-inside: avoid;
  }

  .loan-table {
    font-size: 0.72rem;
  }

  .summary-strip .summary-item .s-value {
    font-size: 0.95rem;
  }

  .report-footer {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-end !important;
    gap: 1.5rem !important;
    page-break-inside: avoid;

    .sign-box {
      flex: 1 1 0 !important;
      width: auto !important;
      max-width: none !important;
    }
  }
}

@media screen and (max-width: 900px) {
  .summary-strip {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
